"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createAppBaseNavigationClient = createAppBaseNavigationClient;
/** Self-contained for identical embedding in platform and local Bridges. */
function createAppBaseNavigationClient(host) {
    const allowed = (control) => control.sourceRoutePaths === undefined ||
        control.sourceRoutePaths.some(path => host.matches(path, host.currentRoute()));
    const control = (id) => {
        const exact = host.controls().find(control => control.id === id);
        if (exact)
            return allowed(exact) ? exact : null;
        const candidates = host
            .controls()
            .filter(control => control.navigationActionId === id && allowed(control));
        if (candidates.length > 1)
            throw new Error('Ambiguous navigation action');
        return candidates[0] ?? null;
    };
    const target = (id, input = {}) => {
        const selected = control(id);
        if (!selected || selected.kind !== 'route')
            throw new Error('Navigation action is not available on this page');
        if (selected.unavailableReason || !selected.routePath)
            return null;
        const record = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);
        if (!record(input) ||
            Object.keys(input).some(key => !['parameters', 'query', 'hash'].includes(key)))
            throw new Error('Invalid navigation input');
        const parameters = input.parameters ?? {};
        if (!record(parameters) || Object.keys(parameters).length > 16)
            throw new Error('Invalid navigation parameters');
        const used = new Set();
        const path = selected.routePath
            .split('/')
            .map(segment => {
            if (segment === ':me')
                return 'me';
            if (!segment.startsWith(':'))
                return segment;
            const key = segment.slice(1), value = Object.prototype.hasOwnProperty.call(parameters, key)
                ? parameters[key]
                : undefined;
            if ((typeof value !== 'string' && typeof value !== 'number') ||
                (typeof value === 'number' && !Number.isFinite(value)))
                throw new Error('Missing navigation parameter: ' + key);
            const text = String(value);
            if (!text ||
                text.length > 512 ||
                ['.', '..'].includes(text) ||
                /[\\/\u0000-\u001f]/.test(text))
                throw new Error('Invalid navigation parameter: ' + key);
            used.add(key);
            return encodeURIComponent(text);
        })
            .join('/');
        if (Object.keys(parameters).some(key => !used.has(key)))
            throw new Error('Unknown navigation parameter');
        const url = new URL(path, 'https://navigation.invalid');
        if (url.origin !== 'https://navigation.invalid' ||
            !path.startsWith('/') ||
            path.startsWith('//') ||
            !host.matches(selected.routePath, url.pathname))
            throw new Error('Invalid navigation target');
        const query = input.query ?? {};
        if (!record(query) || Object.keys(query).length > 32)
            throw new Error('Invalid navigation query');
        for (const [key, value] of Object.entries(query)) {
            if (!key ||
                key.length > 128 ||
                !['string', 'number', 'boolean'].includes(typeof value) ||
                (typeof value === 'number' && !Number.isFinite(value)) ||
                String(value).length > 2048)
                throw new Error('Invalid navigation query');
            url.searchParams.set(key, String(value));
        }
        if (input.hash !== undefined) {
            if (typeof input.hash !== 'string' ||
                input.hash.length > 512 ||
                /[\u0000-\u001f]/.test(input.hash))
                throw new Error('Invalid navigation fragment');
            url.hash = input.hash;
        }
        const resolved = url.pathname + url.search + url.hash;
        const href = host.href(resolved);
        return {
            controlId: selected.id,
            path: resolved,
            href,
            attributes: {
                href,
                'data-sl-control': selected.id,
                ...(selected.navigation
                    ? { 'data-sl-navigation': selected.navigation }
                    : {}),
            },
        };
    };
    const bind = (element, id, input = {}) => {
        if (element.tagName !== 'A')
            throw new Error('Navigation binding requires an anchor');
        const resolved = target(id, input);
        if (!resolved) {
            element.removeAttribute('href');
            element.setAttribute('aria-disabled', 'true');
            return null;
        }
        for (const [key, value] of Object.entries(resolved.attributes))
            element.setAttribute(key, value);
        element.removeAttribute('aria-disabled');
        return resolved;
    };
    return { control, target, bind };
}
